/**
 * Warms the smart-link store for the chat `<ContextBanner>` URL so the
 * inline SmartCard mounts already `'resolved'` and the
 * `smart-card-inline-card` UFO hold never registers.
 */
import { useMemo } from 'react';

import { useSmartLinkContext } from '@atlaskit/link-provider';
import { ACTION_RESOLVED, cardAction } from '@atlaskit/linking-common';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useEmptyChatLaunchSelector } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useFireOncePerKeyOnRender } from '@atlassian/conversation-assistant-utils/useFireOncePerKeyOnRender';

import { useCurrentPageAsContext } from '../../common/ui/chat-input-action-buttons/current-page-as-context/useCurrentPageAsContext';
import { ENTITY_CONTEXT_TYPE } from '../../types';
import { shouldRenderContextBannerSmartCard } from '../../ui/chat-prompt-input/context-banner/shouldRenderContextBannerSmartCard';
import { useContextBannerSmartLinkInfo } from '../../ui/chat-prompt-input/context-banner/useContextBannerSmartLinkInfo';

// Resolved JsonLd body, or null on any failure. Errors collapse to null —
// only success acts here; SmartCard's baseline flow handles all error UI.
type ResolvedBody = unknown | null;

// Singleflight: promises are reusable, so one Map entry covers both
// in-flight and settled.
const warmResolveCache = new Map<string, Promise<ResolvedBody>>();

const getOrCreateWarmResolve = (url: string): Promise<ResolvedBody> => {
	let promise = warmResolveCache.get(url);
	if (!promise) {
		promise = fetchResolveBatch(url);
		warmResolveCache.set(url, promise);
	}
	return promise;
};

/**
 * Direct POST instead of `client.fetchData` — the public API goes through a
 * dataloader with a ~250 ms throttle that defeats the head-start window.
 * Endpoint shape mirrors `CardClient.postBatchResolve` (private).
 */
const fetchResolveBatch = async (url: string): Promise<ResolvedBody> => {
	try {
		const response = await fetch('/gateway/api/object-resolver/resolve/batch', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'origin-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
			credentials: 'include',
			body: JSON.stringify([{ resourceUrl: url }]),
		});
		if (!response.ok) {
			return null;
		}
		const data = (await response.json().catch(() => undefined)) as
			| Array<{ status: number; body?: unknown }>
			| undefined;
		const first = Array.isArray(data) ? data[0] : undefined;
		if (first?.status === 200 && 'body' in first) {
			return first.body;
		}
		return null;
	} catch {
		return null;
	}
};

export interface WarmChatContextBannerLinkProps {
	/** Mirrors `<ChatPromptInput>` `enableBrowserContextBanner` prop. */
	enableBrowserContextBanner?: boolean;
	/** Mirrors the outer `<ChatPromptInput>` `useCurrentPageContext` render guard. */
	useCurrentPageContext?: boolean;
	/** Fully-derived `<ChatPromptInput>` space-scoped conversation render guard, including selected-space state when relevant. */
	isSpaceScopedConversation?: boolean;
}

export const WarmChatContextBannerLink = ({
	enableBrowserContextBanner,
	useCurrentPageContext = true,
	isSpaceScopedConversation,
}: WarmChatContextBannerLinkProps): null => {
	const smartLinkCtx = useSmartLinkContext();
	const experience = useAIMateExperienceTracker();
	const [{ projectContext }] = useEmptyChatLaunchSelector();
	const { currentContext } = useCurrentPageAsContext();

	const sharedContext = useMemo(() => {
		if (projectContext?.projectUrl) {
			return {
				type: ENTITY_CONTEXT_TYPE,
				url: projectContext.projectUrl,
				name: projectContext.projectName,
			};
		}

		return currentContext;
	}, [currentContext, projectContext?.projectName, projectContext?.projectUrl]);
	// Browser-context URL arrives async after warm time; force current-page/entity URL derivation here.
	const { displayUrl: url } = useContextBannerSmartLinkInfo({
		currentContext: sharedContext,
		hasBrowserContext: false,
		browserContextUrl: undefined,
	});
	const store = smartLinkCtx?.store;
	const client = smartLinkCtx?.connections?.client;

	// Browser-ext URL (`browserContext.browserUrl`) populates async after warm
	// time; skip rather than warm the wrong URL.
	const isBrowserContextBannerFlow = !!enableBrowserContextBanner;
	const hasActiveContextForWarm =
		!!projectContext?.projectUrl || currentContext?.type === 'current_page';
	const shouldRenderBannerSmartCard = shouldRenderContextBannerSmartCard({
		useCurrentPageContext,
		isSpaceScopedConversation,
		hasActiveEditorContext: hasActiveContextForWarm,
	});

	const storeHasUrl = !!store && store.getState()[url] !== undefined;
	const shouldWarm =
		!!url &&
		!!store &&
		!!client &&
		shouldRenderBannerSmartCard &&
		!isBrowserContextBannerFlow &&
		// One-shot snapshot (no subscription); the consumer's store wiring
		// handles later changes.
		!storeHasUrl;

	// Render-time fire is intentional: `<SmartCard>` calls `client.fetchData`
	// from a render-time `useResolve` hook, so post-commit paths start too
	// late. Strict-Mode safety is preserved by the module-level
	// `warmResolveCache` singleflight (the second fire is a no-op).
	useFireOncePerKeyOnRender(shouldWarm, 'initial-context-banner-url', () => {
		const targetUrl = url!;
		const targetStore = store!;
		experience.start({
			id: ExperienceName.WARM_CONTEXT_BANNER_LINK,
			name: ExperienceName.WARM_CONTEXT_BANNER_LINK,
		});
		void (async () => {
			const body = await getOrCreateWarmResolve(targetUrl);
			if (body !== null) {
				// Cast at boundary: ResolvedBody is `unknown` to avoid a `@atlaskit/json-ld-types` dep.
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				targetStore.dispatch(cardAction(ACTION_RESOLVED, { url: targetUrl }, body as any));
				experience.succeed({ name: ExperienceName.WARM_CONTEXT_BANNER_LINK });
			} else {
				experience.abort({
					name: ExperienceName.WARM_CONTEXT_BANNER_LINK,
					reason: 'warm-resolve-failed',
				});
			}
		})();
	});

	return null;
};
